import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Player } from "../../../components/molecules/clip/player";
import { IframeViewModel } from "./ViewModel";
import { useRecoilState, useSetRecoilState } from "recoil";
import { PartnerViewSession } from "../../State";
import DrunkLogo from "../../../assets/images/misc/logo-404a.png";
import { PageHeader } from "../../../components/molecules/pageHeader";
import { Box, Flex, Image } from "rebass/styled-components";
import { TestViewer } from "./Components/TestViewer";
import { useTranslation } from "react-i18next";
import { useUser } from "../../Hooks/useUser";
import { CrossSitePromotion } from "../CrossSitePromotion";
import { ECrossSitePromotionType } from "../../@types";
import Lottie from "lottie-react";

import loading from "../../../animations/loading.json";
import { TransitTime } from "../../Components/TransitTime";
import { ClipPruned } from "../../Components/Clip/Components/ClipPruned";
import { buildLink, getUserDisplayName } from "./Utilities";
import { analytics } from "MVVM/Analytics";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { NotificationBannerCopyType } from "MVVM/State/iframe";

const domain = window.location.origin;

const AllstarIframe = () => {
  const [clip, setClip] = useState<any>({
    loading: true,
    data: {
      _id: false,
      shareId: false,
      user: {
        _id: false,
      },
      tags: [],
    },
  });

  const { allstarUser } = useUser();
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const testView = params.get("testView");
  let clipId = params.get("clip");
  const platform =
    params.get("platform") !== "null" ? params.get("platform") : null;
  const partnerRequestedBy =
    platform &&
    !["GC2022", "LEETIFY"].includes(platform.toUpperCase()) && // skip old leetify and gamersclub
    !allstarUser?.isAdmin;
  const useCase = params.get("useCase");
  const background = params.get("background");
  const posterImage = params.get("pi");
  const loop = params.get("loop");
  const placeholderId = params.get("static");
  const mid = params.get("mid");
  const pid = params.get("pid");
  const autoplay = params.get("autoplay");
  const volume = Number(params.get("volume")) ?? 100;
  const muted = params.get("muted");
  const [partnerViewSession, setPartnerViewSession] =
    useRecoilState(PartnerViewSession);
  const shouldAutoplay =
    [autoplay, background].includes("true") || autoplay !== "false";
  const shouldMute = muted === "false" ? false : shouldAutoplay;
  const shouldAllowInlinePlayback = !!params.get("inline");
  const [shouldPoll, setShouldPoll] = useState(params.get("poll"));
  const showControls = !background;
  const shouldLoop = background || loop || placeholderId;
  const source = undefined;
  const { t } = useTranslation("translation", { keyPrefix: "partnerOverlay" });
  const hidePrunedClipControls = platform === "L202211" && background;
  const setNotificationBannerCopyType = useSetRecoilState(
    NotificationBannerCopyType,
  );

  const {
    notificationData,
    getPlaceholderData,
    getSpectatedClip,
    clip: { getClip, createPrunedClip },
  } = IframeViewModel(clipId, useCase, platform, mid, pid);

  const analyticsContext = useAnalyticsContext(platform);

  const recoverPrunedClip = async () => {
    await createPrunedClip({
      variables: {
        clipIdentifier: clip?.data?._id,
      },
    });
    let newClip = (await getClip({
      variables: {
        clipIdentifier: clipId,
      },
    })) as any;

    newClip = newClip?.data?.getClip as any;
    setPartnerViewSession({
      ...partnerViewSession,
      clip: newClip,
    });
    setShouldPoll("recover");
    return setClip({
      loading: false,
      hasBeenPruned: newClip?.status === "Pruned",
      onDemand: newClip?.onDemand,
      data: newClip,
    });
  };

  const fetchClip: (isRefetch?: boolean) => Promise<void> = useCallback(
    async (isRefetch?: boolean) => {
      (async function () {
        if (isRefetch && !shouldPoll) return;

        if (useCase === "SPECTATE") {
          try {
            let specClip = (await getSpectatedClip(
              platform || "",
              mid || "",
              pid || "",
            )) as any;
            if (specClip._id) {
              clipId = specClip._id;
            } else {
              return setClip({
                ...specClip,
                status: 404,
                error: false,
                loading: false,
              });
            }
          } catch (e) {
            console.error(e);
            return setClip({
              status: e,
              error: true,
              loading: false,
            });
          }
        }

        try {
          const shouldFetchClip =
            (!clip?.data?._id &&
              partnerViewSession.readyToFetchClip &&
              !placeholderId &&
              !clip?.stillSpectating) ||
            isRefetch;

          if (shouldFetchClip) {
            const fetchInput = { clipIdentifier: clipId } as any;

            if (partnerRequestedBy) {
              fetchInput.partnerRequestedBy = platform;
            }

            let clip = (await getClip({
              variables: {
                ...fetchInput,
              },
            })) as any;
            clip = clip?.data?.getClip as any;

            if (!clip?._id) {
              return setClip({
                status: 404,
                error: true,
                loading: false,
              });
            }

            setPartnerViewSession({
              ...partnerViewSession,
              clip: clip,
            });

            return setClip({
              loading: false,
              hasBeenPruned: clip?.status === "Pruned",
              onDemand: clip?.onDemand,
              data: clip,
            });
          }
        } catch (e) {
          console.error(e);
          return setClip({
            status: e,
            error: true,
            loading: false,
          });
        }
      })();
    },
    [
      clip?.data?._id,
      clipId,
      getClip,
      partnerRequestedBy,
      partnerViewSession,
      placeholderId,
      platform,
      setPartnerViewSession,
      shouldPoll,
    ],
  );

  async function getPlaceholder(placeholderId: string) {
    const staticData = await getPlaceholderData(placeholderId);

    if (staticData.status === 404) {
      return setClip({ error: true, loading: false });
    }
    return setClip({
      loading: false,
      data: {
        _id: placeholderId,
        fallbackHLS: staticData.resource,
        placeholderThumbnail: staticData.thumbnail,
      },
    });
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchClip();
  }, [
    clipId,
    partnerViewSession.readyToFetchClip,
    partnerRequestedBy,
    fetchClip,
  ]);

  useEffect(() => {
    if (placeholderId && clip.loading) {
      getPlaceholder(placeholderId);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const LoadDynamicCtaData = () => {
    if (!partnerViewSession?.overlayConfig?.display) return false;

    if (!partnerViewSession.fetchedSuggested) {
      if (
        !partnerViewSession.overlayConfig.knownUser &&
        (partnerViewSession.steamIdForClaim || partnerViewSession.puuidForClaim)
      ) {
        notificationData.requestNotificationData();
      } else if (partnerViewSession.overlayConfig.knownUser) {
        if (!partnerViewSession?.promotion?.display) {
          setNotificationBannerCopyType("default");
          setPartnerViewSession({
            ...partnerViewSession,
            dynamicCta: {
              buttonText: t("viewOnAllstar"),
              link: `${domain}/clip?clip=${clipId}`,
              mainCopy: t("centerText", {
                partner:
                  partnerViewSession?.clip?.partner?.partnerId?.displayName,
              }),
              type: "",
              isClaimable: false,
            },
            notificationBanner: {
              link: buildLink({
                url: `${domain}/login`,
                platform: platform,
              }),
              thumbnail: "",
              text: (
                <>
                  {t(
                    "partnerOverlay.notificationBanner.default",
                    "Create montages, add FX, earn sponsorship and more",
                  )}
                </>
              ),
              copy: "Create montages, add FX, earn sponsorship and more",
              buttonText: t(
                "partnerOverlay.notificationBanner.default.buttonText",
                "Go to Allstar.gg",
              ),
            },
          });
        } else {
          if (platform && ["gamersclub", "GC2022"].includes(platform)) {
            setNotificationBannerCopyType("promotion");
            setPartnerViewSession({
              ...partnerViewSession,
              dynamicCta: {
                buttonText: t(`${partnerViewSession.promotion.buttonCopy}`),
                link: buildLink({
                  url: `${partnerViewSession.promotion.link}`,
                }),
                mainCopy: t(`${partnerViewSession.promotion.subtitle}`),
                type: "",
                isClaimable: false,
              },
              notificationBanner: {
                link: buildLink({
                  url: partnerViewSession.promotion.link,
                }),
                thumbnail: partnerViewSession.promotion.image,
                text: (
                  <>
                    {t(
                      "partnerOverlay.clashCompetition.name",
                      "Join the Clash Competition to win rare CS2 skins & gift cards!",
                    )}
                  </>
                ),
                copy: "Join the Clash Competition to win rare CS2 skins & gift cards!",
                buttonText: t(
                  "partnerOverlay.clashCompetition.buttonCopy",
                  "Join Here",
                ),
              },
            });
          }
        }
      } else {
        setNotificationBannerCopyType("default");
        setPartnerViewSession({
          ...partnerViewSession,
          dynamicCta: {
            buttonText: t("signupForAllstar"),
            link: buildLink({
              url: `${domain}/register`,
              game: partnerViewSession?.clip?.game,
              numClaimableClips: partnerViewSession?.claimableCount,
              username: getUserDisplayName(partnerViewSession),
              platform: platform,
            }),
            mainCopy: t("centerText", {
              partner:
                partnerViewSession?.clip?.partner?.partnerId?.displayName,
            }),
            type: "",
            isClaimable: false,
          },
          notificationBanner: {
            link: buildLink({
              url: `${domain}/register`,
              platform: platform,
            }),
            thumbnail: "",
            text: (
              <>
                {t(
                  "partnerOverlay.notificationBanner.default",
                  "Create montages, add FX, earn sponsorship and more",
                )}
              </>
            ),
            copy: "Create montages, add FX, earn sponsorship and more",
            buttonText: t(
              "partnerOverlay.notificationBanner.default.buttonText",
              "Go to Allstar.gg",
            ),
          },
        });
      }
    }
  };

  useEffect(() => {
    if (partnerViewSession?.promotion?.display) LoadDynamicCtaData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerViewSession?.promotion?.display]);

  if (!clip.stillSpectating && (clip.error || notificationData.error)) {
    const message = notificationData.error
      ? "Oops! Something went wrong."
      : "Oops! Content not found.";

    return (
      <StyledWrapper>
        <PageHeader
          z1
          title={message}
          image={<Image src={DrunkLogo} mb={7} maxWidth="180px" />}
        />
      </StyledWrapper>
    );
  }

  if (testView) return <TestViewer />;
  if (clip.loading)
    return (
      <>
        <StyledWrapper
          justifyContent={"center"}
          alignItems={"center"}
          data-testid="loading-wrapper"
        >
          <Lottie
            animationData={loading}
            style={{ height: 100, width: 100 }}
            loop={true}
          />
        </StyledWrapper>
      </>
    );

  if (
    ["Submitted", "Queued", "Error"].includes(clip?.data?.status) ||
    (["SPECTATE"].includes(useCase || "") && clip.stillSpectating)
  )
    return (
      <StyledWrapper
        justifyContent={"center"}
        alignItems={"center"}
        data-testid="spectating-wrapper"
      >
        <MaxWidthContainer>
          <ClipThumbnail
            snapshot={`${process.env.REACT_APP_S3_CLIP_BUCKET}/${clip?.data?.clipImageThumb}`}
          />
          <Box
            backgroundColor={clip.stillSpectating ? "transparent" : "navy"}
            style={{ borderRadius: "9px" }}
          >
            <TransitTime
              clip={clip?.data?._id}
              refetchClip={fetchClip}
              spectating={clip?.spectatingTime}
              clipStatus={clip?.data?.status}
            />
          </Box>
        </MaxWidthContainer>
      </StyledWrapper>
    );

  if (clip?.hasBeenPruned)
    return (
      <StyledWrapper
        data-testid="pruned-wrapper"
        justifyContent={"center"}
        alignItems={"center"}
      >
        <MaxWidthContainer>
          <ClipThumbnail
            snapshot={`${process.env.REACT_APP_S3_CLIP_BUCKET}/${clip?.data?.clipImageThumb}`}
          />
          {!hidePrunedClipControls && (
            <ClipPruned
              onClick={recoverPrunedClip}
              onDemand={clip?.onDemand}
              clipTitle={clip?.data?.clipTitle}
              clipId={clip?._id}
            />
          )}
        </MaxWidthContainer>
      </StyledWrapper>
    );

  return (
    <>
      <CrossSitePromotion type={ECrossSitePromotionType.VideoPlayerCTA} />
      <StyledPlayer
        autoplay={shouldAutoplay}
        muted={shouldMute}
        hideBigPlay={shouldAutoplay}
        controls={showControls}
        loop={shouldLoop}
        playsinline={shouldAllowInlinePlayback}
        embedded={true}
        platform={platform}
        source={source}
        clip={clip.data}
        loading={clip.loading}
        volume={volume}
        posterImage={posterImage}
        className="iframeWrapper"
        onError={(player: videojs.VideoJsPlayer) => {
          const error = player.error();
          const createdDate = new Date(clip.data.createdDate);
          const now = new Date();
          const minutesSinceCreation =
            (now.valueOf() - createdDate.valueOf()) / 60000;

          analytics.track("Iframe Error", {
            errorMessage: error?.message,
            errorCode: error?.code,
            minutesSinceCreation: minutesSinceCreation,
            ...analyticsContext,
          });
        }}
        onPause={() => {
          LoadDynamicCtaData();
        }}
        onEnd={() => {
          LoadDynamicCtaData();
        }}
        onLoadedData={async (sessionId: string) => {
          if (placeholderId) {
            analytics.page("Static Placeholder View", {
              session_id: sessionId,
              placeholderId: placeholderId,
              ...analyticsContext,
            });
          }
        }}
      />
    </>
  );
};

export default AllstarIframe;

const MaxWidthContainer = styled(Box)`
  max-width: 700px;
`;

const StyledPlayer = styled(Player)`
  .video-js {
    height: 100vh !important;
    width: 100vw !important;
  }
  .vjs-tech {
    height: 100% !important;
    width: 100% !important;
  }
`;

const StyledWrapper = styled(Flex)`
  height: 100vh !important;
  width: 100vw !important;
  justify-content: center;
  align-items: center;
`;

const ClipThumbnail = styled<any>(Box)`
  position: absolute;
  justify-content: center;
  align-items: center;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-image: url("${(props: any) => props.snapshot}"),
    url("https://media.allstar.gg/static/fallback.jpg");
  background-size: cover;
  opacity: 0.1;
  z-index: -1;
`;
